import React, { useState, useEffect, useRef } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import { useUrlState } from '../../hooks/useUrlState';
import { formatCountry } from '../../utils/countryFormat';
import ConfirmDialog from '../common/ConfirmDialog';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Tabs,
  Tab,
  TablePagination,
  Autocomplete,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteForever as DeleteForeverIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Business as SupplierIcon,
  ArrowBack as BackIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Inventory as ProductIcon,
  CloudUpload as ImportIcon,
  CloudDownload as ExportIcon,
  EditNote as BulkEditIcon
} from '@mui/icons-material';
import {
  getSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  deactivateSupplier,
  deleteSupplierPermanent,
  getSupplierPerformance,
  addSupplierProduct,
  bulkImportSuppliers,
  bulkUpdateSuppliers,
  exportSuppliersCsv,
  formatCurrency,
  formatNumber,
  formatDate,
  getStatusColor,
  getSupplierTypeLabel,
  getPaymentMethodLabel,
  getPurchaseOrders,
  getSupplierBankStatement
} from '../../services/procurementService';
import EntityActionsMenu from '../common/EntityActionsMenu';
import BulkImportDialog from '../common/BulkImportDialog';
import BulkUpdateDialog from '../common/BulkUpdateDialog';
import { getWarehouses } from '../../services/inventoryService';
import { StatusBadge, PageHeader, ActionBar, Timeline, EntityChip } from '../ds';
import { useEntityTimeline } from '../../services/auditTimelineService';
import SupplierPaymentMilestones from './SupplierPaymentMilestones';
import {
  Print as PrintIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

// "USD 551,013.02": the code once, not the code plus a symbol (DT-1861, per-currency Total Spend).
// A blank or unknown code (Intl throws RangeError) shows the number with whatever code was stored, never a crash.
const formatCurrencyCode = (value, currency) => {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, currencyDisplay: 'code' }).format(Number(value) || 0);
  } catch (e) {
    const n = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);
    return currency ? `${currency} ${n}` : n;
  }
};

function Suppliers() {
  const { confirmDialogProps, confirm } = useConfirmDialog();

  // Guard the create/edit dialog: closing it via the backdrop, Escape or
  // Cancel used to discard a part-filled supplier with no warning.
  const [formTouched, setFormTouched] = useState(false);
  const guardedCloseDialog = useUnsavedChangesGuard(formTouched, confirm);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // List view state — filters round-trip through the URL via useUrlState so
  // navigating into a supplier detail and back (browser Back) restores the
  // filter set. Pre-fix these were plain useState, so editing a supplier from
  // a filtered list wiped the filter on return (Rishabh, 2026-05-27).
  const [suppliers, setSuppliers] = useState([]);
  // >0 when the server holds more suppliers than this page fetched, which would
  // make the client-side search incomplete. 0 = everything is loaded.
  const [truncated, setTruncated] = useState(0);
  const [searchTerm, setSearchTerm] = useUrlState('q', '');
  const [typeFilter, setTypeFilter] = useUrlState('type', '');
  const [statusFilter, setStatusFilter] = useUrlState('status', '');
  const [page, setPage] = useUrlState('p', 0, { parse: (v) => parseInt(v, 10) || 0 });
  const [rowsPerPage, setRowsPerPage] = useUrlState('pp', 25, { parse: (v) => parseInt(v, 10) || 25 });

  // Stash the list's filter query-string so the detail's Back arrow returns to
  // the SAME filtered list, not the whole unfiltered list (Rishabh 2026-07-02).
  useEffect(() => {
    if (!id) {
      try { sessionStorage.setItem('scm.suppliersListSearch', window.location.search || ''); } catch (_) { /* private mode */ }
    }
  }, [id, searchTerm, typeFilter, statusFilter, page, rowsPerPage]);

  // Detail view state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  // Purchase History tab: cache POs per supplier so re-clicking the tab
  // doesn't refetch. Cleared on supplier change.
  const [supplierPOs, setSupplierPOs] = useState([]);
  const [loadingSupplierPOs, setLoadingSupplierPOs] = useState(false);
  const [supplierStatement, setSupplierStatement] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({
    supplier_code: '',
    supplier_name: '',
    supplier_type: 'product',
    // These map directly to suppliers table columns; backend allowedFields in
    // updateSupplier (suppliers/service.js) is the canonical list.
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    finance_contact_name: '',
    finance_contact_email: '',
    finance_contact_phone: '',
    website: '',
    billing_address_line1: '',
    billing_city: '',
    billing_postal_code: '',
    billing_country: '',
    payment_terms: 'Net 30',
    payment_method: 'bank_transfer',
    currency: '',
    credit_limit: '',
    tax_id: '',
    notes: '',
    purchase_terms_html: '',
    default_warehouse_code: '',
    is_active: true,
    // When true (only honored on create), backend also creates a virtual
    // warehouse with the same name (type='supplier') and points the supplier's
    // default_warehouse_code at it so PO entry auto-fills.
    create_as_warehouse: false,
  });
  const [warehouses, setWarehouses] = useState([]);

  // Milestones saver — SupplierPaymentMilestones component registers a saver
  // function (or null when nothing's changed) via useRef so handleSave can
  // call it as part of the supplier-update transaction. Persisting milestones
  // separately keeps the supplier-update endpoint focused on the suppliers
  // table (no schema churn) while still saving everything in one Save click.
  const paymentMilestonesSaverRef = React.useRef(null);

  // Bulk actions: row selection + dialog visibility.
  // importMode = 'insert' (Import new) | 'update' (Update from CSV — for the
  // export → edit → re-import round-trip). The same dialog component handles
  // both flows; it switches its required-column logic on `mode`.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState('insert');
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);

  const [productForm, setProductForm] = useState({
    sku: '',
    product_name: '',
    unit_cost: '',
    currency: '',
    moq: '',
    lead_time_days: '',
    is_preferred: false
  });

  // from=contacts deep-link: render Back as "Back to Contacts" instead of
  // going to the supplier list. Preserved here so the detail-mode PageHeader
  // can read it without re-parsing the URL on every render.
  const fromContacts = new URLSearchParams(location.search).get('from') === 'contacts';

  useEffect(() => {
    if (id) {
      loadSupplierDetails(id);
    } else {
      loadSuppliers();
    }
    // Warehouses needed by the supplier edit dialog for the default-receipt picker.
    getWarehouses({ is_active: true, limit: 200 })
      .then(r => setWarehouses(Array.isArray(r?.data) ? r.data : (Array.isArray(r?.warehouses) ? r.warehouses : (Array.isArray(r) ? r : []))))
      .catch(() => setWarehouses([]));

    // Deep-link from /contacts → "Add contact" → Supplier opens this page
    // with ?new=1. Auto-open the existing Add Supplier dialog and strip the
    // query param so a back-button reload doesn't re-open it.
    if (!id && new URLSearchParams(location.search).get('new') === '1') {
      handleOpenDialog();
      navigate('/procurement/suppliers', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Deep-link from /contacts Edit icon (?edit=1) auto-opens the edit dialog
  // once supplier details have loaded. Fires only on first detail load.
  useEffect(() => {
    if (!id || !selectedSupplier) return;
    if (new URLSearchParams(location.search).get('edit') !== '1') return;
    handleOpenDialog(selectedSupplier);
    // Strip edit=1 so a re-render or refresh doesn't keep re-opening the
    // dialog. Preserve from=contacts so back-button still routes correctly.
    const p = new URLSearchParams(location.search);
    p.delete('edit');
    navigate({ search: p.toString() ? `?${p.toString()}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplier?.id]);

  // Deep-link to a specific detail tab, e.g. ?tab=payments opens the
  // "Statement / Payments" tab directly (used when arriving from the
  // Outstanding Bills list on the Payments page). Fires once the supplier
  // detail has loaded; keyed on the supplier id so it doesn't fight manual
  // tab clicks (those don't change the URL).
  useEffect(() => {
    if (!id || !selectedSupplier) return;
    const t = new URLSearchParams(location.search).get('tab');
    if (!t) return;
    const tabIndex = { details: 0, products: 1, history: 2, purchases: 2, statement: 3, payments: 3 };
    if (tabIndex[t] === undefined) return;
    setDetailTab(tabIndex[t]);
    // Strip ?tab after applying it (mirrors the ?edit handler above) so a later
    // reload or a since-clicked tab isn't re-forced back to it. Other params kept.
    const p = new URLSearchParams(location.search);
    p.delete('tab');
    navigate({ search: p.toString() ? `?${p.toString()}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplier?.id]);

  const loadSuppliers = async () => {
    setLoading(true);
    setError(null);

    try {
      // limit=1000 matches the validator ceiling. Server default is 25 which
      // makes the client-side search/pagination on this page useless — it
      // can only see the first 25 alphabetically. We want the full list so
      // local search hits everything.
      const params = { limit: 1000 };
      if (typeFilter) params.supplier_type = typeFilter;
      if (statusFilter !== '') params.is_active = statusFilter === 'active';

      const res = await getSuppliers(params);
      // Handle response structure: { success, data: suppliers_array }
      const suppliersData = res?.data;
      const rows = Array.isArray(suppliersData) ? suppliersData : [];
      setSuppliers(rows);
      // Search + pagination on this page are client-side, which is only correct
      // while the whole set fits in one fetch (223 suppliers vs limit 1000 as of
      // 2026-07-27). If the company ever grows past the ceiling, the rows beyond
      // it would silently vanish from search — an operator would conclude a
      // supplier doesn't exist. Surface that instead of hiding it; the fix at
      // that point is server-side search (the backend already accepts `search`,
      // `limit` and `offset` and returns pagination.total, as Invoices now uses).
      const serverTotal = Number(res?.pagination?.total ?? rows.length);
      setTruncated(serverTotal > rows.length ? serverTotal : 0);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      setError('Failed to load suppliers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Only the newest supplier load may apply its answer: an older one landing late (the user opened another supplier, or went
  // back to the list) would show the wrong supplier under this URL (DT-1139, same as Transfers DT-1113).
  const detailRequestRef = useRef(0);
  useEffect(() => { if (!id) detailRequestRef.current += 1; }, [id]);

  const loadSupplierDetails = async (supplierId) => {
    const request = ++detailRequestRef.current;
    const stale = () => request !== detailRequestRef.current;
    // A different record: drop the previous one now, so a failed load never leaves it on screen under this URL.
    setSelectedSupplier(prev => (prev && String(prev.id) === String(supplierId) ? prev : null));
    setLoading(true);
    setError(null);

    try {
      const [supplierRes, perfRes] = await Promise.all([
        getSupplier(supplierId),
        getSupplierPerformance(supplierId)
      ]);
      if (stale()) return;

      setSelectedSupplier(supplierRes?.data);
      setSupplierProducts(supplierRes?.data?.products || []);
      setPerformance(perfRes?.data);
      // Reset Purchase History + Statement caches so a different supplier's data
      // doesn't bleed through; the tab effects re-fetch on demand.
      setSupplierPOs([]);
      setSupplierStatement(null);
    } catch (err) {
      if (stale()) return;
      console.error('Failed to load supplier details:', err);
      setError('Failed to load supplier details. Please try again.');
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  // Lazy-load POs when the operator opens the Purchase History tab. Cached
  // for the lifetime of this supplier detail view; cleared on supplier change.
  useEffect(() => {
    if (detailTab !== 2 || !selectedSupplier?.id) return;
    if (supplierPOs.length > 0) return; // already loaded
    let cancelled = false;
    setLoadingSupplierPOs(true);
    (async () => {
      try {
        const r = await getPurchaseOrders({ supplier_id: selectedSupplier.id, limit: 100 });
        if (cancelled) return;
        // listPurchaseOrders returns { purchase_orders, total, limit, offset } —
        // NOT a bare array. Earlier shape mismatch crashed the table render
        // with a blank screen because supplierPOs ended up as an object and
        // .map blew up. Always unwrap the purchase_orders array (with a fallback
        // to plain array in case some future caller flattens the shape).
        const list = Array.isArray(r?.data?.purchase_orders)
          ? r.data.purchase_orders
          : (Array.isArray(r?.data) ? r.data : []);
        setSupplierPOs(list);
      } catch (e) {
        console.error('Failed to load supplier POs:', e);
        if (!cancelled) setSupplierPOs([]);
      } finally {
        if (!cancelled) setLoadingSupplierPOs(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab, selectedSupplier?.id]);

  // Lazy-load the bank-payment statement when the Statement / Payments tab
  // opens. Cached for the lifetime of this supplier detail view.
  useEffect(() => {
    if (detailTab !== 3 || !selectedSupplier?.id) return;
    if (supplierStatement) return; // already loaded
    let cancelled = false;
    setLoadingStatement(true);
    (async () => {
      try {
        const r = await getSupplierBankStatement(selectedSupplier.id);
        if (cancelled) return;
        setSupplierStatement(r?.data || null);
      } catch (e) {
        console.error('Failed to load supplier bank statement:', e);
        if (!cancelled) {
          setSupplierStatement({ payments: [], paid_by_currency: [], open_pos: [], open_by_currency: [] });
        }
      } finally {
        if (!cancelled) setLoadingStatement(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab, selectedSupplier?.id]);

  const handleOpenDialog = (supplier = null) => {
    setFormTouched(false); // fresh dialog is never dirty
    if (supplier) {
      setEditingSupplier(supplier);
      setSupplierForm({
        supplier_code: supplier.supplier_code || '',
        supplier_name: supplier.supplier_name || '',
        supplier_type: supplier.supplier_type || 'product',
        primary_contact_name: supplier.primary_contact_name || '',
        primary_contact_email: supplier.primary_contact_email || '',
        primary_contact_phone: supplier.primary_contact_phone || '',
        finance_contact_name: supplier.finance_contact_name || '',
        finance_contact_email: supplier.finance_contact_email || '',
        finance_contact_phone: supplier.finance_contact_phone || '',
        website: supplier.website || '',
        billing_address_line1: supplier.billing_address_line1 || '',
        billing_city: supplier.billing_city || '',
        billing_postal_code: supplier.billing_postal_code || '',
        billing_country: supplier.billing_country || '',
        payment_terms: supplier.payment_terms || 'Net 30',
        payment_method: supplier.payment_method || 'bank_transfer',
        currency: supplier.currency || '', // keep unknown as unknown; never save an assumed GBP
        credit_limit: supplier.credit_limit || '',
        tax_id: supplier.tax_id || '',
        notes: supplier.notes || '',
        purchase_terms_html: supplier.purchase_terms_html || '',
        default_warehouse_code: supplier.default_warehouse_code || '',
        is_active: supplier.is_active !== false
      });
    } else {
      setEditingSupplier(null);
      setSupplierForm({
        supplier_code: '',
        supplier_name: '',
        supplier_type: 'product',
        primary_contact_name: '',
        primary_contact_email: '',
        primary_contact_phone: '',
        finance_contact_name: '',
        finance_contact_email: '',
        finance_contact_phone: '',
        website: '',
        billing_address_line1: '',
        billing_city: '',
        billing_postal_code: '',
        billing_country: '',
        payment_terms: 'Net 30',
        payment_method: 'bank_transfer',
        currency: '', // the user picks it: a company's default is its reporting currency, not what its suppliers bill in
        credit_limit: '',
        tax_id: '',
        notes: '',
        purchase_terms_html: '',
        default_warehouse_code: '',
        is_active: true
      });
    }
    setError(null); setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (!editingSupplier && !supplierForm.currency) {
        setError('Pick the currency this supplier bills in.');
        return;
      }
      if (editingSupplier) {
        // create_as_warehouse is honored on update too — only when the
        // supplier doesn't already have a default_warehouse_code (the UI gates
        // the toggle on that). Backend defends with the same check.
        const r = await updateSupplier(editingSupplier.id, supplierForm);
        const wh = r?.data?.warehouse;
        const whErr = r?.data?.warehouse_error;
        if (whErr) {
          setSuccess(`Supplier updated. (Warehouse step: ${whErr})`);
        } else if (wh) {
          setSuccess(`Supplier updated and warehouse "${wh.name}" (${wh.code}) created — linked as default.`);
        } else {
          setSuccess('Supplier updated successfully');
        }
      } else {
        const r = await createSupplier(supplierForm);
        const wh = r?.data?.warehouse;
        const whErr = r?.data?.warehouse_error;
        if (whErr) {
          setSuccess(`Supplier created. (Warehouse step failed: ${whErr})`);
        } else if (wh) {
          setSuccess(`Supplier created with linked warehouse ${wh.code} (${wh.name})`);
        } else {
          setSuccess('Supplier created successfully');
        }
      }

      // Bundle the milestones save with the supplier save — only fires when
      // the milestones editor flagged a change (paymentMilestonesSaverRef is
      // null otherwise so we skip the network call). Validation errors come
      // back as 400 and we surface them but DON'T treat them as a full
      // supplier-save failure (the supplier update already succeeded above).
      const milestonesSaver = paymentMilestonesSaverRef.current;
      if (milestonesSaver && editingSupplier) {
        try {
          await milestonesSaver();
          setSuccess(prev => (prev || '') + ' Payment milestones saved.');
        } catch (mErr) {
          setError(`Supplier saved, but payment milestones failed: ${mErr?.response?.data?.error || mErr.message}`);
          return; // keep dialog open so operator can fix and retry milestones
        }
      }

      setFormTouched(false); // saved — closing is safe now
      setDialogOpen(false);
      if (id) {
        loadSupplierDetails(id);
      } else {
        loadSuppliers();
      }
      // Came from /contacts Edit icon — return there after save.
      if (fromContacts && editingSupplier) {
        navigate('/contacts');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save supplier');
    }
  };

  const handleDeactivate = async (supplierId) => {
    if (!await confirm({
      title: 'Deactivate Supplier',
      message: 'Are you sure you want to deactivate this supplier?',
      confirmText: 'Deactivate',
      severity: 'warning'
    })) return;

    try {
      await deactivateSupplier(supplierId);
      setSuccess('Supplier deactivated successfully');
      loadSuppliers();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to deactivate supplier');
    }
  };

  // Hard delete. Backend returns 409 if any PO references the supplier; in that
  // case we offer the soft-delete (deactivate) as a fallback so the operator
  // doesn't get stuck.
  const handleDeletePermanent = async (supplier) => {
    if (!await confirm({
      title: `Delete ${supplier.supplier_name}?`,
      message: 'This permanently removes the supplier. Only works if no purchase orders reference them.',
      confirmText: 'Delete permanently',
      severity: 'error',
    })) return;

    try {
      await deleteSupplierPermanent(supplier.id);
      setSuccess(`${supplier.supplier_name} deleted`);
      loadSuppliers();
    } catch (err) {
      const code = err.response?.data?.error_code;
      const msg = err.response?.data?.error || err.message || 'Failed to delete supplier';
      if (code === 'SUPPLIER_HAS_POS') {
        const fallback = await confirm({
          title: 'Has purchase orders',
          message: `${msg}\n\nDeactivate instead?`,
          confirmText: 'Deactivate',
          severity: 'warning',
        });
        if (fallback) await handleDeactivate(supplier.id);
      } else {
        setError(msg);
      }
    }
  };

  // === Bulk actions =====================================================

  // Reuse current list filters so the export mirrors what's on screen.
  const handleExport = async () => {
    try {
      const params = {};
      if (typeFilter) params.supplier_type = typeFilter;
      if (statusFilter !== '') params.is_active = statusFilter === 'active';
      if (searchTerm) params.search = searchTerm;
      const res = await exportSuppliersCsv(params);
      setSuccess(`Exported ${res.filename}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Export failed');
    }
  };

  // Dispatch on importMode: 'insert' → bulk-import endpoint (creates new rows),
  // 'update' → bulk-update endpoint (reuses same dialog, matches rows by id).
  // Cell-level rule for update mode: empty CSV cells are SKIPPED, not blanked
  // — so an operator who exports, edits two cells, and re-imports doesn't
  // accidentally clear the other 30 columns. To explicitly clear a field,
  // type the sentinel string __CLEAR__ in that cell.
  const handleBulkImport = async (rows) => {
    if (importMode === 'update') {
      const updates = rows.map((r) => ({ ...r, id: parseInt(r.id, 10) }));
      const result = await bulkUpdateSuppliers(updates);
      await loadSuppliers();
      return result;
    }
    const result = await bulkImportSuppliers(rows);
    await loadSuppliers();
    return result;
  };

  const handleBulkUpdate = async (updates) => {
    const result = await bulkUpdateSuppliers(updates);
    setSelectedIds(new Set());
    await loadSuppliers();
    return result;
  };

  // Deactivate-many via the existing single-row endpoint (no separate backend
  // path — fewer surfaces to test, and deactivation already idempotent).
  const handleBulkDeactivate = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Deactivate ${ids.length} supplier${ids.length === 1 ? '' : 's'}?`,
      message: `They will be hidden from pickers but PO history is preserved. You can reactivate later via Bulk Update → is_active.`,
      confirmText: 'Deactivate',
      confirmColor: 'error',
    });
    if (!ok) return;
    try {
      const updates = ids.map((id) => ({ id, is_active: 0 }));
      const result = await bulkUpdateSuppliers(updates);
      setSuccess(`Deactivated ${result.updated ?? ids.length} supplier${ids.length === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      await loadSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Bulk deactivate failed');
    }
  };

  // Column spec for the import dialog. Mirrors the backend's allowedFields in
  // updateSupplier so every writable column round-trips. Header aliases match
  // common CIN7 / QB exports so operators can reuse existing spreadsheets.
  const SUPPLIER_IMPORT_COLUMNS = [
    { field: 'supplier_code', label: 'Supplier code', required: true,
      aliases: ['code', 'vendor_code', 'vendor code', 'supplier code'] },
    { field: 'supplier_name', label: 'Supplier name', required: true,
      aliases: ['name', 'vendor_name', 'vendor name', 'supplier name', 'company'] },
    { field: 'supplier_type', label: 'Type',
      aliases: ['type', 'category'],
      enum: ['product', 'service', 'raw_material', 'packaging'] },
    { field: 'tax_id', label: 'Tax ID / VAT', aliases: ['vat', 'tax_number', 'vat number'] },
    { field: 'primary_contact_name', label: 'Contact name', aliases: ['contact', 'contact_name'] },
    { field: 'primary_contact_email', label: 'Contact email', type: 'email', aliases: ['email'] },
    { field: 'primary_contact_phone', label: 'Contact phone', aliases: ['phone', 'tel'] },
    // Billing address (canonical for the supplier record itself)
    { field: 'billing_address_line1', label: 'Billing address 1', aliases: ['address', 'address1', 'billing address 1'] },
    { field: 'billing_address_line2', label: 'Billing address 2', aliases: ['address2', 'billing address 2'] },
    { field: 'billing_city', label: 'Billing city', aliases: ['city'] },
    { field: 'billing_state', label: 'Billing state', aliases: ['state', 'region', 'province'] },
    { field: 'billing_country', label: 'Billing country', aliases: ['country', 'country_code'] },
    { field: 'billing_postal_code', label: 'Billing postcode', aliases: ['zip', 'postcode', 'postal_code'] },
    // Shipping address (where goods physically come from — often different)
    { field: 'shipping_address_line1', label: 'Shipping address 1', aliases: ['ship_address1', 'pickup_address'] },
    { field: 'shipping_address_line2', label: 'Shipping address 2', aliases: ['ship_address2'] },
    { field: 'shipping_city', label: 'Shipping city', aliases: ['ship_city'] },
    { field: 'shipping_state', label: 'Shipping state', aliases: ['ship_state'] },
    { field: 'shipping_country', label: 'Shipping country', aliases: ['ship_country'] },
    { field: 'shipping_postal_code', label: 'Shipping postcode', aliases: ['ship_postcode', 'ship_zip'] },
    // Payment + banking
    { field: 'payment_terms', label: 'Payment terms', aliases: ['terms'] },
    { field: 'payment_method', label: 'Payment method',
      enum: ['bank_transfer', 'check', 'cash', 'credit_card'] },
    { field: 'currency', label: 'Currency', aliases: ['ccy'] },
    { field: 'bank_name', label: 'Bank name' },
    { field: 'bank_account_number', label: 'Bank account number', aliases: ['account_number', 'iban'] },
    { field: 'bank_routing_number', label: 'Routing number', aliases: ['routing', 'sort_code'] },
    { field: 'swift_code', label: 'SWIFT / BIC', aliases: ['swift', 'bic'] },
    // Credit + ops
    { field: 'credit_limit', label: 'Credit limit', type: 'number' },
    { field: 'credit_rating', label: 'Credit rating', aliases: ['rating'] },
    { field: 'default_warehouse_code', label: 'Default warehouse',
      aliases: ['warehouse', 'warehouse_code'] },
    { field: 'purchase_terms_html', label: 'PO T&Cs (HTML)',
      multiline: true,
      aliases: ['terms_html', 'po_terms'] },
    { field: 'notes', label: 'Notes', aliases: ['comments', 'remarks'] },
  ];

  // Sample rows for the example file — two realistic suppliers so operators
  // can see what valid data looks like without having to reverse-engineer it.
  const SUPPLIER_IMPORT_SAMPLE = [
    {
      supplier_code: 'SUP-001',
      supplier_name: 'Acme Manufacturing Co.',
      supplier_type: 'product',
      tax_id: 'GB123456789',
      primary_contact_name: 'Jane Smith',
      primary_contact_email: 'jane@acme.example',
      primary_contact_phone: '+44 20 7946 0958',
      billing_address_line1: '12 Bond Street',
      billing_city: 'London',
      billing_state: '',
      billing_country: 'GB',
      billing_postal_code: 'W1S 4AB',
      payment_terms: 'Net 30',
      payment_method: 'bank_transfer',
      currency: 'GBP',
      credit_limit: 50000,
      notes: 'Primary box-supplier for Q1 launch',
    },
    {
      supplier_code: 'SUP-002',
      supplier_name: 'Hangzhou Foil Print Ltd',
      supplier_type: 'packaging',
      tax_id: '',
      primary_contact_name: 'Niall Lin',
      primary_contact_email: 'niall@hzfp.example',
      primary_contact_phone: '',
      billing_address_line1: '88 Yuhang Rd',
      billing_city: 'Hangzhou',
      billing_state: 'Zhejiang',
      billing_country: 'CN',
      billing_postal_code: '310000',
      payment_terms: '50% deposit, 50% on shipment',
      payment_method: 'bank_transfer',
      currency: 'USD',
      credit_limit: 0,
      notes: '',
    },
  ];

  // Fields available in Bulk Update — covers every safe-to-bulk-edit field.
  // Per-row identity (supplier_code) and address rows excluded by intent: those
  // are nearly always unique and changing in bulk usually means "I picked the
  // wrong filter". Per-row edit is the right surface for them.
  const SUPPLIER_BULK_EDITABLE = [
    { field: 'supplier_type', label: 'Type', type: 'enum',
      enum: ['product', 'service', 'raw_material', 'packaging'] },
    { field: 'tax_id', label: 'Tax ID / VAT' },
    { field: 'payment_terms', label: 'Payment terms', placeholder: 'e.g. Net 30' },
    { field: 'payment_method', label: 'Payment method', type: 'enum',
      enum: ['bank_transfer', 'check', 'cash', 'credit_card'] },
    { field: 'currency', label: 'Currency', placeholder: 'GBP / USD / EUR' },
    { field: 'credit_limit', label: 'Credit limit', type: 'number' },
    { field: 'credit_rating', label: 'Credit rating' },
    { field: 'default_warehouse_code', label: 'Default warehouse code',
      help: 'Pre-fills PO entry. Must match an existing warehouse code.' },
    { field: 'purchase_terms_html', label: 'PO T&Cs (HTML)', multiline: true,
      help: 'Tier 2 of the T&C precedence chain in PO PDFs. HTML allowed.' },
    { field: 'is_active', label: 'Status', type: 'boolean',
      help: 'Yes = visible in pickers, No = soft-deactivated.' },
    { field: 'notes', label: 'Notes', multiline: true },
  ];

  const handleAddProduct = async () => {
    try {
      setError(null);
      await addSupplierProduct(selectedSupplier.id, productForm);
      setSuccess('Product added successfully');
      setProductDialogOpen(false);
      setProductForm({
        sku: '',
        product_name: '',
        unit_cost: '',
        currency: '',
        moq: '',
        lead_time_days: '',
        is_preferred: false
      });
      loadSupplierDetails(selectedSupplier.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add product');
    }
  };

  // Filter suppliers
  const filteredSuppliers = suppliers.filter(supplier => {
    const searchLower = searchTerm.toLowerCase();
    return (
      supplier.supplier_code?.toLowerCase().includes(searchLower) ||
      supplier.supplier_name?.toLowerCase().includes(searchLower) ||
      supplier.primary_contact_name?.toLowerCase().includes(searchLower)
    );
  });

  // Activity timeline — must be called unconditionally (rules-of-hooks).
  const { events: supplierTimelineEvents, loading: supplierTimelineLoading } = useEntityTimeline(
    'procurement_suppliers_route',
    selectedSupplier?.id || null
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Detail View
  if (id && selectedSupplier) {
    return (
      <Box>
        <PageHeader
          backTo={fromContacts ? '/contacts' : `/procurement/suppliers${(() => { try { return sessionStorage.getItem('scm.suppliersListSearch') || ''; } catch (_) { return ''; } })()}`}
          primary={selectedSupplier.supplier_name}
          slug={selectedSupplier.supplier_code}
          metadata={[
            { label: getSupplierTypeLabel(selectedSupplier.supplier_type) },
            ...(selectedSupplier.default_warehouse_code ? [{
              label: `Default WH: ${selectedSupplier.default_warehouse_name || selectedSupplier.default_warehouse_code}`,
              tooltip: selectedSupplier.default_warehouse_name ? selectedSupplier.default_warehouse_code : null,
            }] : []),
            ...(selectedSupplier.currency ? [{ label: selectedSupplier.currency }] : []),
          ]}
          statusBadge={<StatusBadge status={selectedSupplier.is_active ? 'active' : 'inactive'} size="medium" />}
          actions={
            <ActionBar
              primaryCount={1}
              actions={[
                { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(selectedSupplier) },
                { key: 'copy', label: 'Copy supplier code', icon: <CopyIcon fontSize="small" />, onClick: () => navigator.clipboard?.writeText(selectedSupplier.supplier_code || '') },
                ...(selectedSupplier.primary_contact_email ? [{
                  key: 'email',
                  label: 'Email contact',
                  icon: <EmailIcon fontSize="small" />,
                  onClick: () => { window.location.href = `mailto:${selectedSupplier.primary_contact_email}`; },
                }] : []),
                { key: 'print', label: 'Print', icon: <PrintIcon fontSize="small" />, onClick: () => window.print() },
              ]}
            />
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Total Orders</Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatNumber(performance?.order_stats?.total_orders || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Total Spend</Typography>
                {/* One value per PO currency, never added across currencies (DT-1861), as "USD 551,013.02". */}
                {(performance?.order_stats?.by_currency || []).length === 0 ? (
                  <Typography variant="h4" fontWeight="bold">
                    {selectedSupplier.currency ? formatCurrencyCode(0, selectedSupplier.currency) : formatNumber(0)}
                  </Typography>
                ) : performance.order_stats.by_currency.map((c) => (
                  <Typography
                    key={c.currency}
                    variant={performance.order_stats.by_currency.length === 1 ? 'h4' : 'h6'}
                    fontWeight="bold"
                    title={`${c.orders} order${c.orders === 1 ? '' : 's'} in ${c.currency}`}
                  >
                    {formatCurrencyCode(c.total_value, c.currency)}
                  </Typography>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">On-Time Delivery</Typography>
                <Typography variant="h4" fontWeight="bold">
                  {performance?.delivery_performance?.on_time_rate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">Quality Rate</Typography>
                <Typography variant="h4" fontWeight="bold">
                  {performance?.quality_metrics?.quality_rate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Activity rail — audit_log entries for this supplier */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>Activity</Typography>
            <Timeline
              events={supplierTimelineEvents.map(e => ({
                ...e,
                timestamp: e.timestamp ? formatDate(e.timestamp) + ' ' + (String(e.timestamp).split(' ')[1] || '').slice(0, 5) : '',
              }))}
              loading={supplierTimelineLoading}
              empty={<Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>No activity yet.</Typography>}
            />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card sx={{ mt: 3 }}>
          <Tabs
            value={detailTab}
            onChange={(e, v) => setDetailTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="Details" />
            <Tab label="Products" />
            <Tab label="Purchase History" />
            <Tab label="Statement / Payments" />
          </Tabs>

          <CardContent>
            {/* Details Tab */}
            {detailTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Primary Contact</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {selectedSupplier.primary_contact_name && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SupplierIcon fontSize="small" color="action" />
                        <Typography>{selectedSupplier.primary_contact_name}</Typography>
                      </Box>
                    )}
                    {selectedSupplier.primary_contact_email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography>{selectedSupplier.primary_contact_email}</Typography>
                      </Box>
                    )}
                    {selectedSupplier.primary_contact_phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography>{selectedSupplier.primary_contact_phone}</Typography>
                      </Box>
                    )}
                  </Box>

                  {(selectedSupplier.finance_contact_name || selectedSupplier.finance_contact_email || selectedSupplier.finance_contact_phone) && (
                    <>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>Finance Contact</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {selectedSupplier.finance_contact_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SupplierIcon fontSize="small" color="action" />
                            <Typography>{selectedSupplier.finance_contact_name}</Typography>
                          </Box>
                        )}
                        {selectedSupplier.finance_contact_email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography>{selectedSupplier.finance_contact_email}</Typography>
                          </Box>
                        )}
                        {selectedSupplier.finance_contact_phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography>{selectedSupplier.finance_contact_phone}</Typography>
                          </Box>
                        )}
                      </Box>
                    </>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: (selectedSupplier.finance_contact_name || selectedSupplier.finance_contact_email || selectedSupplier.finance_contact_phone) ? 2 : 0 }}>
                    {selectedSupplier.website && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WebsiteIcon fontSize="small" color="action" />
                        <Typography>{selectedSupplier.website}</Typography>
                      </Box>
                    )}
                    {(selectedSupplier.billing_address_line1 || selectedSupplier.billing_city || selectedSupplier.billing_postal_code || selectedSupplier.billing_country) && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography>
                          {[selectedSupplier.billing_address_line1, selectedSupplier.billing_city, selectedSupplier.billing_postal_code, formatCountry(selectedSupplier.billing_country)]
                            .filter(Boolean).join(', ')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Payment Terms</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Payment Terms</Typography>
                      <Typography fontWeight="medium">{selectedSupplier.payment_terms || 'Not set'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Payment Method</Typography>
                      <Typography fontWeight="medium">
                        {getPaymentMethodLabel(selectedSupplier.payment_method) || 'Not set'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Currency</Typography>
                      <Typography fontWeight="medium">{selectedSupplier.currency || '—'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Credit Limit</Typography>
                      <Typography fontWeight="medium">
                        {selectedSupplier.credit_limit
                          ? (selectedSupplier.currency ? formatCurrency(selectedSupplier.credit_limit, selectedSupplier.currency) : formatNumber(selectedSupplier.credit_limit))
                          : 'No limit'}
                      </Typography>
                    </Box>
                    {selectedSupplier.tax_id && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary">Tax ID</Typography>
                        <Typography fontWeight="medium">{selectedSupplier.tax_id}</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                {selectedSupplier.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Notes</Typography>
                    <Typography>{selectedSupplier.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Products Tab */}
            {detailTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    Supplier Products ({supplierProducts.length})
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => { setProductForm(f => ({ ...f, currency: f.currency || selectedSupplier?.currency || '' })); setError(null); setProductDialogOpen(true); }}
                  >
                    Add Product
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Product Name</TableCell>
                        <TableCell align="right">Unit Cost</TableCell>
                        <TableCell align="right">MOQ</TableCell>
                        <TableCell align="right">Lead Time</TableCell>
                        <TableCell>Preferred</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supplierProducts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Box sx={{ py: 4 }}>
                              <ProductIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                              <Typography color="textSecondary">
                                No products linked to this supplier
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        supplierProducts.map((product) => (
                          <TableRow key={product.id} hover>
                            <TableCell>
                              <Typography fontWeight="medium">{product.sku}</Typography>
                            </TableCell>
                            <TableCell>{product.product_name || '—'}</TableCell>
                            <TableCell align="right">
                              {product.currency ? formatCurrency(product.unit_cost, product.currency) : formatNumber(product.unit_cost)}
                            </TableCell>
                            <TableCell align="right">{formatNumber(product.moq) || '—'}</TableCell>
                            <TableCell align="right">
                              {product.lead_time_days ? `${product.lead_time_days} days` : '—'}
                            </TableCell>
                            <TableCell>
                              {product.is_preferred && (
                                <Chip label="Preferred" size="small" color="primary" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Purchase History Tab — lists every PO that has this supplier
                as supplier_id, newest first. Click the PO number to open the
                full PO detail page. */}
            {detailTab === 2 && (
              <Box>
                {loadingSupplierPOs ? (
                  <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                    Loading purchase history…
                  </Typography>
                ) : supplierPOs.length === 0 ? (
                  <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                    No purchase orders found for this supplier yet.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>PO #</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Warehouse</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Received qty</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {supplierPOs.map(po => (
                          <TableRow key={po.id} hover>
                            <TableCell>
                              <Typography component="a"
                                onClick={() => navigate(`/procurement/purchase-orders/${po.id}`)}
                                sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline dotted', fontFamily: 'monospace', fontSize: 13 }}>
                                {po.po_number || po.id}
                              </Typography>
                            </TableCell>
                            <TableCell>{po.po_date ? formatDate(po.po_date) : '—'}</TableCell>
                            <TableCell>{po.warehouse_code || '—'}</TableCell>
                            <TableCell>
                              <StatusBadge status={po.status || 'open'} />
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                              {po.currency || ''} {formatNumber(Number(po.total_amount || 0))}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {formatNumber(Number(po.received_qty_total || 0))} / {formatNumber(Number(po.ordered_qty_total || 0))}
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined"
                                onClick={() => navigate(`/procurement/purchase-orders/${po.id}`)}>
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Statement / Payments Tab — actual bank payments (source of truth =
                the bank) plus open PO obligations, both grouped by currency.
                Backed by supplier_bank_payments (see backend/scripts/ap-reconciliation). */}
            {detailTab === 3 && (
              <Box>
                {loadingStatement ? (
                  <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                    Loading statement…
                  </Typography>
                ) : !supplierStatement ? (
                  <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                    No statement available.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Per-currency summary */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>Paid (from bank)</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(supplierStatement.paid_by_currency || []).length === 0 ? (
                            <Typography variant="body2" color="textSecondary">No bank payments matched.</Typography>
                          ) : (supplierStatement.paid_by_currency || []).map(c => (
                            <Chip key={`paid-${c.currency}`} color="success" variant="outlined"
                              label={`${c.currency} ${formatNumber(Number(c.total_paid || 0))} · ${c.txns} txns`} />
                          ))}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>Open to pay (POs)</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(supplierStatement.open_by_currency || []).length === 0 ? (
                            <Typography variant="body2" color="textSecondary">No open POs.</Typography>
                          ) : (supplierStatement.open_by_currency || []).map(c => (
                            <Chip key={`open-${c.currency}`} color="warning" variant="outlined"
                              label={`${c.currency} ${formatNumber(Number(c.open_total || 0))} · ${c.po_count} POs`} />
                          ))}
                        </Box>
                      </Box>
                    </Box>

                    {/* Bank payment transactions */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                        Bank payments ({(supplierStatement.payments || []).length})
                      </Typography>
                      {(supplierStatement.payments || []).length === 0 ? (
                        <Typography color="textSecondary" sx={{ py: 2 }}>
                          No bank payments matched to this supplier.
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell>Date</TableCell>
                                <TableCell>Bank source</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(supplierStatement.payments || []).map((p, i) => (
                                <TableRow key={`${p.bank_source}-${p.bank_transaction_id}-${i}`} hover>
                                  <TableCell>{p.payment_date ? formatDate(p.payment_date) : '—'}</TableCell>
                                  <TableCell>
                                    <Chip size="small" variant="outlined" label={p.bank_source || '—'} />
                                    {p.source_table && p.source_table.indexOf('queue') !== -1 && (
                                      <Chip size="small" variant="outlined" label="queue" sx={{ ml: 0.5 }} />
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                    {p.matched_pattern || p.bank_transaction_id || '—'}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {p.currency || ''} {formatNumber(Number(p.amount || 0))}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>

                    {/* Open PO obligations */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                        Open obligations ({(supplierStatement.open_pos || []).length})
                      </Typography>
                      {(supplierStatement.open_pos || []).length === 0 ? (
                        <Typography color="textSecondary" sx={{ py: 2 }}>
                          No open or partially-received POs.
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell>PO #</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(supplierStatement.open_pos || []).map((po, i) => (
                                <TableRow key={`${po.po_number}-${i}`} hover>
                                  <TableCell>
                                    <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                      {po.po_number || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{po.po_date ? formatDate(po.po_date) : '—'}</TableCell>
                                  <TableCell><StatusBadge status={po.status || 'open'} /></TableCell>
                                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {po.currency || ''} {formatNumber(Number(po.total_amount || 0))}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Payments are reconstructed from matched bank transactions (the complete record of money actually paid). Each row is shown in its native currency.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Add Product Dialog */}
        <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Supplier Product</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="SKU"
                value={productForm.sku}
                onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Product Name"
                value={productForm.product_name}
                onChange={(e) => setProductForm({ ...productForm, product_name: e.target.value })}
                fullWidth
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Unit Cost"
                    type="number"
                    value={productForm.unit_cost}
                    onChange={(e) => setProductForm({ ...productForm, unit_cost: e.target.value })}
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{productForm.currency}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={productForm.currency}
                      label="Currency"
                      onChange={(e) => setProductForm({ ...productForm, currency: e.target.value })}
                    >
                      {['USD','GBP','EUR','AUD','SGD','CAD','NZD','CHF','HKD','JPY','CNY'].map(c => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="MOQ"
                    type="number"
                    value={productForm.moq}
                    onChange={(e) => setProductForm({ ...productForm, moq: e.target.value })}
                    fullWidth
                    helperText="Minimum Order Quantity"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Lead Time (days)"
                    type="number"
                    value={productForm.lead_time_days}
                    onChange={(e) => setProductForm({ ...productForm, lead_time_days: e.target.value })}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={productForm.is_preferred}
                    onChange={(e) => setProductForm({ ...productForm, is_preferred: e.target.checked })}
                  />
                }
                label="Preferred Supplier for this SKU"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProductDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddProduct}
              disabled={!productForm.sku || !productForm.unit_cost}
            >
              Add Product
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Supplier Dialog */}
        {renderSupplierDialog()}
        <ConfirmDialog {...confirmDialogProps} />
      </Box>
    );
  }

  // List View
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Suppliers
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your supplier relationships and product catalogs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Supplier
          </Button>
          <EntityActionsMenu
            label="Actions"
            selectedCount={selectedIds.size}
            actions={[
              {
                key: 'import',
                label: 'Import new suppliers',
                hint: 'CSV or Excel — drag-drop + validation preview',
                icon: <ImportIcon fontSize="small" />,
                onClick: () => { setImportMode('insert'); setImportOpen(true); },
                group: 'Data',
              },
              {
                key: 'export',
                label: 'Export current view',
                hint: 'Downloads a CSV of all rows matching current filters',
                icon: <ExportIcon fontSize="small" />,
                onClick: handleExport,
                group: 'Data',
              },
              {
                key: 'update-from-csv',
                label: 'Update from CSV',
                hint: 'Export → edit in Excel → re-upload. Matches rows by id.',
                icon: <BulkEditIcon fontSize="small" />,
                onClick: () => { setImportMode('update'); setImportOpen(true); },
                group: 'Data',
              },
              {
                key: 'bulk-update',
                label: 'Bulk update selected',
                hint: 'Pick fields to overwrite, preview diff, apply',
                icon: <BulkEditIcon fontSize="small" />,
                onClick: () => setBulkUpdateOpen(true),
                requiresSelection: true,
                group: 'Edit',
              },
              {
                key: 'bulk-deactivate',
                label: 'Deactivate selected',
                hint: 'Soft-delete: hides them from pickers but preserves PO history',
                icon: <DeleteForeverIcon fontSize="small" />,
                onClick: handleBulkDeactivate,
                requiresSelection: true,
                dangerous: true,
                group: 'Edit',
              },
            ]}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={loadSuppliers}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Supplier Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Supplier Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="product">Product Supplier</MenuItem>
                  <MenuItem value="service">Service Provider</MenuItem>
                  <MenuItem value="raw_material">Raw Material</MenuItem>
                  <MenuItem value="packaging">Packaging</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="outlined" onClick={loadSuppliers} fullWidth>
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Suppliers Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.size > 0 && selectedIds.size < filteredSuppliers.length}
                      checked={filteredSuppliers.length > 0 && selectedIds.size === filteredSuppliers.length}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedIds(new Set(filteredSuppliers.map(s => s.id)));
                        else setSelectedIds(new Set());
                      }}
                      title="Select all on this page"
                    />
                  </TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Supplier Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Payment Terms</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((supplier) => (
                    <TableRow
                      key={supplier.id}
                      hover
                      selected={selectedIds.has(supplier.id)}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/procurement/suppliers/${supplier.id}`)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(supplier.id)}
                          onChange={(e) => {
                            setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(supplier.id);
                              else next.delete(supplier.id);
                              return next;
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          component="a"
                          onClick={(e) => { e.stopPropagation(); navigate(`/procurement/suppliers/${supplier.id}`); }}
                          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {supplier.supplier_code}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{supplier.supplier_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getSupplierTypeLabel(supplier.supplier_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {supplier.primary_contact_name || supplier.primary_contact_email || '—'}
                      </TableCell>
                      <TableCell>{supplier.payment_terms || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={supplier.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={supplier.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(supplier);
                          }}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePermanent(supplier);
                          }}
                          title="Delete (only if no POs reference this supplier)"
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredSuppliers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ py: 4 }}>
                        <SupplierIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography color="textSecondary">
                          No suppliers found
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenDialog()}
                          sx={{ mt: 2 }}
                        >
                          Add First Supplier
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {truncated > 0 && (
            <Alert severity="warning" sx={{ m: 2 }}>
              Showing the first {suppliers.length} of {truncated} suppliers — search on this
              page only looks at the ones loaded, so some may not appear. Narrow the Type or
              Status filter to bring the rest into view.
            </Alert>
          )}
          <TablePagination
            component="div"
            count={filteredSuppliers.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Supplier Dialog */}
      {renderSupplierDialog()}
      <ConfirmDialog {...confirmDialogProps} />

      <BulkImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entityLabel="supplier"
        mode={importMode}
        columnSpec={SUPPLIER_IMPORT_COLUMNS}
        sampleRows={SUPPLIER_IMPORT_SAMPLE}
        onSubmit={handleBulkImport}
        helpText={
          importMode === 'update'
            ? 'Round-trip workflow: keep the id column, edit any other cells you want to change, leave blank to skip. Type __CLEAR__ in a cell to explicitly empty it.'
            : 'Required: supplier_code, supplier_name. Header row must be present — column order doesn\'t matter, and headers are matched case-insensitively (e.g. \'Vendor Name\' → supplier_name).'
        }
      />

      <BulkUpdateDialog
        open={bulkUpdateOpen}
        onClose={() => setBulkUpdateOpen(false)}
        entityLabel="supplier"
        selectedRows={filteredSuppliers.filter(s => selectedIds.has(s.id))}
        editableFields={SUPPLIER_BULK_EDITABLE}
        identityColumns={['supplier_code', 'supplier_name']}
        onSubmit={handleBulkUpdate}
      />
    </Box>
  );

  function renderSupplierDialog() {
    return (
      <Dialog
        open={dialogOpen}
        onClose={() => guardedCloseDialog(() => { setFormTouched(false); setDialogOpen(false); })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
        {/* onChangeCapture marks the form dirty so closing warns first. */}
        <DialogContent onChangeCapture={() => setFormTouched(true)}>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="subtitle2" color="textSecondary">Basic Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Supplier Code"
                  value={supplierForm.supplier_code}
                  onChange={(e) => setSupplierForm({ ...supplierForm, supplier_code: e.target.value.toUpperCase() })}
                  required
                  fullWidth
                  placeholder="e.g., SUP001"
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  label="Supplier Name"
                  value={supplierForm.supplier_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, supplier_name: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Supplier Type</InputLabel>
                  <Select
                    value={supplierForm.supplier_type}
                    label="Supplier Type"
                    onChange={(e) => { setFormTouched(true); setSupplierForm({ ...supplierForm, supplier_type: e.target.value }); }}
                  >
                    <MenuItem value="product">Product Supplier</MenuItem>
                    <MenuItem value="service">Service Provider</MenuItem>
                    <MenuItem value="raw_material">Raw Material</MenuItem>
                    <MenuItem value="packaging">Packaging</MenuItem>
                    <MenuItem value="employee">Employee (payroll)</MenuItem>
                    <MenuItem value="contractor">Contractor (monthly)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Tax ID / VAT Number"
                  value={supplierForm.tax_id}
                  onChange={(e) => setSupplierForm({ ...supplierForm, tax_id: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Primary Contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Contact Name"
                  value={supplierForm.primary_contact_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, primary_contact_name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Email"
                  type="email"
                  value={supplierForm.primary_contact_email}
                  onChange={(e) => setSupplierForm({ ...supplierForm, primary_contact_email: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Phone"
                  value={supplierForm.primary_contact_phone}
                  onChange={(e) => setSupplierForm({ ...supplierForm, primary_contact_phone: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Finance Contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Contact Name"
                  value={supplierForm.finance_contact_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, finance_contact_name: e.target.value })}
                  fullWidth
                  helperText="AP / billing contact (if different from primary)"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Email"
                  type="email"
                  value={supplierForm.finance_contact_email}
                  onChange={(e) => setSupplierForm({ ...supplierForm, finance_contact_email: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Phone"
                  value={supplierForm.finance_contact_phone}
                  onChange={(e) => setSupplierForm({ ...supplierForm, finance_contact_phone: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label="Website"
              value={supplierForm.website}
              onChange={(e) => setSupplierForm({ ...supplierForm, website: e.target.value })}
              fullWidth
            />
            <TextField
              label="Address"
              value={supplierForm.billing_address_line1}
              onChange={(e) => setSupplierForm({ ...supplierForm, billing_address_line1: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="City"
                  value={supplierForm.billing_city}
                  onChange={(e) => setSupplierForm({ ...supplierForm, billing_city: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Pin Code"
                  value={supplierForm.billing_postal_code}
                  onChange={(e) => setSupplierForm({ ...supplierForm, billing_postal_code: e.target.value })}
                  fullWidth
                  placeholder="e.g. W1S 4AB, 110001, 90210"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Country"
                  value={supplierForm.billing_country}
                  onChange={(e) => setSupplierForm({ ...supplierForm, billing_country: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Payment Terms</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={['Immediate', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90']}
                  value={supplierForm.payment_terms || ''}
                  onChange={(_, v) => { setFormTouched(true); setSupplierForm({ ...supplierForm, payment_terms: v || '' }); }}
                  onInputChange={(_, v) => setSupplierForm({ ...supplierForm, payment_terms: v || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Payment Terms" placeholder="Pick or type custom (e.g. 2/10 Net 30)" />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={supplierForm.payment_method}
                    label="Payment Method"
                    onChange={(e) => { setFormTouched(true); setSupplierForm({ ...supplierForm, payment_method: e.target.value }); }}
                  >
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth required={!editingSupplier}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={supplierForm.currency}
                    label="Currency"
                    onChange={(e) => { setFormTouched(true); setSupplierForm({ ...supplierForm, currency: e.target.value }); }}
                  >
                    {['USD','GBP','EUR','AUD','SGD','CAD','NZD','CHF','HKD','JPY','CNY'].map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Credit Limit"
              type="number"
              value={supplierForm.credit_limit}
              onChange={(e) => setSupplierForm({ ...supplierForm, credit_limit: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">{supplierForm.currency}</InputAdornment>
              }}
              helperText="Leave empty for no limit"
            />

            {/* Payment Milestones editor — optional multi-tranche structure
                (e.g. 30% deposit / 70% on delivery). Persisted separately via
                payment-terms-service; handleSave bundles the save when dirty. */}
            <SupplierPaymentMilestones
              supplierId={editingSupplier?.id}
              onCollectSaver={(saver) => { paymentMilestonesSaverRef.current = saver; }}
            />

            {/* Default receipt warehouse — when a PO is fulfilled, inventory lands
                at this warehouse code (typically a supplier-branch WH). A separate
                Branch Transfer then moves it onward to our internal warehouses. */}
            <FormControl fullWidth>
              <InputLabel>Default Receipt Warehouse</InputLabel>
              <Select
                value={supplierForm.default_warehouse_code}
                label="Default Receipt Warehouse"
                onChange={(e) => { setFormTouched(true); setSupplierForm({ ...supplierForm, default_warehouse_code: e.target.value }); }}
              >
                <MenuItem value=""><em>None — operator must pick on PO</em></MenuItem>
                {warehouses.map(w => (
                  <MenuItem key={w.code || w.id} value={w.code}>
                    {w.code} — {w.name}{w.country ? ` · ${w.country}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Companion warehouse — available on create AND on edit. Hidden
                on edit when the supplier already has a default_warehouse_code
                (no point offering to re-link; backend defends with the same
                check). Useful when goods drop-ship directly from / to this
                supplier (the supplier IS the receiving location). */}
            {(!editingSupplier || !editingSupplier.default_warehouse_code) && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!supplierForm.create_as_warehouse}
                    onChange={(e) => setSupplierForm({ ...supplierForm, create_as_warehouse: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Also create a warehouse with this supplier's name</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Creates a virtual warehouse (type: supplier) and sets it as this supplier's default. Useful when goods drop-ship directly from / to this supplier.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', m: 0 }}
              />
            )}

            <TextField
              label="Notes"
              value={supplierForm.notes}
              onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />

            <TextField
              label="Default Purchase Order T&Cs (HTML)"
              value={supplierForm.purchase_terms_html}
              onChange={(e) => setSupplierForm({ ...supplierForm, purchase_terms_html: e.target.value })}
              fullWidth
              multiline
              rows={8}
              placeholder="<h2>Terms of Purchase</h2>&#10;<p><strong>1. Payment.</strong> Net 60 from invoice date.</p>&#10;&#10;Leave blank to fall back to the issuing subsidiary's default T&Cs."
              helperText="Negotiated T&Cs specific to this supplier. Rendered as an appendix on every PO PDF issued to them. HTML allowed. PO-level override always wins; if both are blank, the issuing subsidiary's default T&Cs are used; if those are also blank, no T&Cs section appears."
              InputLabelProps={{ shrink: true }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={supplierForm.is_active}
                  onChange={(e) => setSupplierForm({ ...supplierForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => guardedCloseDialog(() => { setFormTouched(false); setDialogOpen(false); })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!supplierForm.supplier_code || !supplierForm.supplier_name}
          >
            {editingSupplier ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default Suppliers;
